import {
  type queryOptions,
  type QueryFunction,
  type QueryKey,
} from "@-/com.npmjs.tanstack__react-query";
import { withWriteClockReadGuard } from "@-/dev.improve.haniverse.lib.database.change";

type UseQueryOptionsResult<QueryData, QueryKeyValue extends QueryKey> = Omit<
  ReturnType<typeof queryOptions<QueryData, Error, QueryData, QueryKeyValue>>,
  "queryFn"
> & {
  queryFn: QueryFunction<QueryData, QueryKeyValue>;
};

type CreateUseQueryOptionsResult<
  QueryOptionsArgs extends readonly unknown[],
  QueryFn extends QueryFunction<unknown, QueryKeyValue>,
  QueryKeyValue extends QueryKey,
> = readonly [
  useQueryOptions: (
    ...args: QueryOptionsArgs
  ) => UseQueryOptionsResult<Awaited<ReturnType<QueryFn>>, QueryKeyValue>,
  queryFn: QueryFn,
];

export function createUseQueryOptions<
  const Args extends readonly unknown[],
  QueryKeyValue extends QueryKey,
  QueryFn extends QueryFunction<unknown, QueryKeyValue>,
>(
  useGetQueryKey: () => (...args: Args) => QueryKeyValue,
  queryFn: QueryFn,
): CreateUseQueryOptionsResult<Args, QueryFn, QueryKeyValue>;

export function createUseQueryOptions<
  const Args extends readonly unknown[],
  QueryKeyValue extends QueryKey,
  QueryFn extends QueryFunction<unknown, QueryKeyValue>,
>(
  useGetQueryKey: (...args: Args) => () => QueryKeyValue,
  queryFn: QueryFn,
): CreateUseQueryOptionsResult<Args, QueryFn, QueryKeyValue>;

export function createUseQueryOptions(
  useGetQueryKey: (
    ...args: readonly unknown[]
  ) => (...queryKeyArgs: readonly unknown[]) => QueryKey,
  queryFn: QueryFunction<unknown, QueryKey>,
) {
  // Guard EVERY read built through this factory, and hand the guarded function
  // out as the standalone `queryFn` too — the `client.fetchQuery({ queryFn,
  // queryKey })` composition path must not be able to bypass it. The guard is a
  // no-op for queries that declared no database dependencies.
  const guardedQueryFn = withWriteClockReadGuard(queryFn);

  const useQueryOptions = (...args: readonly unknown[]) => {
    const getQueryKey = useGetQueryKey(...args);
    return {
      queryFn: guardedQueryFn,
      get queryKey() {
        return getQueryKey(...args);
      },
    };
  };

  return [useQueryOptions, guardedQueryFn] as const;
}
